import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { getOrder } from '../../api/backend';
import { clearSession } from '../../utils/session';
import Navbar from '../Navbar/Navbar';
import PageTitle from '../pagetitle/PageTitle';
import Footer from '../footer/Footer';
import Scrollbar from '../scrollbar/scrollbar';
import logo from '../../images/gattomioNero.png';
import './OrderSuccessPage.css';

const OrderSuccessPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchOrder = async () => {
      if (!orderId) {
        setError('Order ID mancante');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await getOrder(orderId);
        setOrder(response.order || response);
        clearSession();
        setLoading(false);
      } catch (err) {
        console.error('Errore caricamento ordine:', err);
        setError(err.message || 'Impossibile caricare i dettagli dell\'ordine');
        setLoading(false);
      }
    };

    fetchOrder();
  }, [orderId, dispatch]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('it-IT', {
      day: '2-digit', month: 'long', year: 'numeric'
    });
  };

  const formatAmount = (amount) => {
    return '€ ' + parseFloat(amount || 0).toFixed(2).replace('.', ',');
  };

  const getOrderNumber = (id) => `ORD-${String(id).padStart(6, '0')}`;

  if (loading) {
    return (
      <>
        <Navbar hclass={'wpo-site-header'} Logo={logo} />
        <PageTitle pageTitle={'Ordine Completato'} pagesub={'Conferma'} />
        <section className="wpo-order-success-section section-padding">
          <div className="container">
            <div className="success-loading">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Caricamento...</span>
              </div>
              <p>Caricamento dettagli ordine...</p>
            </div>
          </div>
        </section>
        <Footer />
        <Scrollbar />
      </>
    );
  }

  if (error) {
    return (
      <>
        <Navbar hclass={'wpo-site-header'} Logo={logo} />
        <PageTitle pageTitle={'Ordine Completato'} pagesub={'Conferma'} />
        <section className="wpo-order-success-section section-padding">
          <div className="container">
            <div className="success-error">
              <div className="error-alert">{error}</div>
              <button className="success-btn-primary" onClick={() => navigate('/')}>
                Torna alla Home
              </button>
            </div>
          </div>
        </section>
        <Footer />
        <Scrollbar />
      </>
    );
  }

  return (
    <>
      <Navbar hclass={'wpo-site-header'} Logo={logo} />
      <PageTitle pageTitle={'Ordine Completato'} pagesub={'Conferma'} />

      <section className="wpo-order-success-section section-padding">
        <div className="container">

          {/* Success Banner */}
          <div className="success-banner">
            <span className="panel-header">Pagamento ricevuto</span>
            <div className="success-icon-wrap">
              <svg className="success-checkmark" width="56" height="56" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="#FF5B2E" strokeWidth="1.5" fill="none" />
                <path className="check-path" d="M7 12l3 3 7-7" stroke="#FF5B2E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <h2>Grazie per il tuo acquisto!</h2>
            <p className="success-sub">
              Conferma inviata a <strong>{order?.client_email}</strong>
            </p>
          </div>

          {/* Meta strip */}
          <div className="success-meta-strip">
            <div className="meta-item">
              <span className="meta-label">Numero ordine</span>
              <strong className="meta-value">{getOrderNumber(order?.id)}</strong>
            </div>
            <div className="meta-divider" />
            <div className="meta-item">
              <span className="meta-label">Data</span>
              <strong className="meta-value">{formatDate(order?.created_at)}</strong>
            </div>
            <div className="meta-divider" />
            <div className="meta-item">
              <span className="meta-label">Totale</span>
              <strong className="meta-value accent">{formatAmount(order?.amount)}</strong>
            </div>
            <div className="meta-divider" />
            <div className="meta-item">
              <span className="meta-label">Stato</span>
              <span className="badge status-completed">Completato</span>
            </div>
          </div>

          {/* Main grid */}
          <div className="success-grid">

            {/* Prodotti */}
            {order?.order_products && order.order_products.length > 0 && (
              <div className="success-card full-card">
                <div className="card-header">
                  <h4>Prodotti ordinati</h4>
                </div>
                <div className="card-body-table">
                  <div className="table-responsive">
                    <table className="success-table">
                      <thead>
                        <tr>
                          <th>Prodotto</th>
                          <th className="text-center">Qtà</th>
                          <th className="text-end">Prezzo unit.</th>
                          <th className="text-end">Totale</th>
                        </tr>
                      </thead>
                      <tbody>
                        {order.order_products.map((item, index) => (
                          <tr key={index}>
                            <td>
                              <strong>{item.product_name}</strong>
                              {item.product_sku && (
                                <small>SKU: {item.product_sku}</small>
                              )}
                            </td>
                            <td className="text-center">{item.quantity}</td>
                            <td className="text-end">{formatAmount(item.unit_price)}</td>
                            <td className="text-end"><strong>{formatAmount(item.total_price)}</strong></td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td colSpan="3" className="text-end">Totale ordine:</td>
                          <td className="text-end">
                            <strong className="total-accent">{formatAmount(order.amount)}</strong>
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Info cards row */}
            <div className="success-info-row">

              {/* Spedizione */}
              <div className="success-card">
                <div className="card-header">
                  <h4><i className="ti-truck"></i> Spedizione</h4>
                </div>
                <div className="card-body-info">
                  <p><strong>{order?.client_name}</strong></p>
                  <p>{order?.shipping_address}</p>
                  <p>{order?.shipping_city}, {order?.shipping_zip}</p>
                  <p>{order?.shipping_country}</p>
                  {order?.client_phone && (
                    <p className="muted">Tel: {order.client_phone}</p>
                  )}
                </div>
              </div>

              {/* Pagamento */}
              <div className="success-card">
                <div className="card-header">
                  <h4><i className="ti-credit-card"></i> Pagamento</h4>
                </div>
                <div className="card-body-info">
                  <p><strong>PayPal</strong></p>
                  <p className="muted paypal-id">{order?.paypal_order_id}</p>
                  <div className="payment-badge">
                    <i className="ti-check"></i> Pagamento confermato
                  </div>
                </div>
              </div>

            </div>

            {/* Azioni */}
            <div className="success-actions">
              <button className="success-btn-primary" onClick={() => navigate('/shop')}>
                <i className="ti-bag"></i> Continua lo shopping
              </button>
              <Link to="/orders" className="success-btn-outline">
                <i className="ti-list"></i> I miei ordini
              </Link>
              <button className="success-btn-ghost" onClick={() => navigate('/')}>
                Torna alla Home
              </button>
            </div>

          </div>
        </div>
      </section>

      <Footer />
      <Scrollbar />
    </>
  );
};

export default OrderSuccessPage;
